import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type {
  Pedido,
  FaturamentoPeriodo,
  CentralDeComando,
  OrigemVendaParceiro,
  CupomMaisUsado,
  Cliente,
} from '@pgcustom/shared-types';
import { ETAPA_CONCLUSAO } from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgChart, PgChartPoint, PgKpiCard, PgModal } from '@pgcustom/design-system';
import { logger } from '../../core/logger.js';
import type { Resultado } from '../../core/resultado.js';
import { rotuloStatusPedido, STATUS_PEDIDO } from '../pedidos/status-pedido.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import { ETAPAS_ROTULO } from '../producao/etapa-ops.js';
import type { DashboardApi, Contagem, ValorMonetario, ItemAviso, ResumoEstoque } from './dashboard.api.js';
import { bucketarContagem, bucketarMedia } from './dashboard.api.js';
import type { RelatoriosApi } from '../relatorios/relatorios.api.js';
import type { PermissoesRelatorios } from '../relatorios/relatorios.permissoes.js';
import { BLOCOS_PERSONALIZAVEIS, carregarBlocosVisiveis, salvarBlocosVisiveis } from './dashboard.preferencias.js';
import type { SocietarioApi } from '../societario/societario.api.js';
import type { PermissoesBonusPg } from '../bonus-pg/bonus-pg.permissoes.js';
import type { ClientesApi } from '../clientes/clientes.api.js';
import { CSS_SITE_TRAFEGO, HTML_CARD_SITE_TRAFEGO, ligarCardSiteTrafego, cardSiteTrafegoSemPermissao } from '../relatorios/site-trafego.card.js';

/**
 * Consolidação do Dashboard (Sprint 15, `DECISIONS.md` DEC-035) +
 * camada visual "PG Custom ERP profissional" pedida pelo owner nesta
 * sessão (ver `DECISIONS.md`, entrada mais recente, para o registro
 * completo da auditoria/decisões). Regra que não mudou desde DEC-035 e
 * continua valendo para todo bloco novo desta sessão: **um mesmo
 * indicador tem uma definição única** — a antiga tabela "Faturamento
 * por período" foi removida por ser o mesmo dado de "Evolução
 * financeira" (gráfico de linha) em outro formato; nenhum indicador
 * novo foi inventado, só apresentado com mais hierarquia visual
 * (ícone, tendência, sparkline) quando havia série real por trás.
 */
const CENTAVOS_POR_REAL = 100;

interface SecaoConfig {
  id: string;
  titulo: string;
}

const SECOES: SecaoConfig[] = [
  { id: 'evolucao-financeira', titulo: 'Evolução financeira' },
  { id: 'pedidos-status', titulo: 'Distribuição de pedidos' },
  { id: 'producao', titulo: 'Produção' },
  { id: 'origem-vendas', titulo: 'Origem de vendas por parceiro' },
  { id: 'heatmap-envios', titulo: 'Envios despachados' },
  { id: 'pedidos-recentes', titulo: 'Pedidos recentes' },
  { id: 'atividades', titulo: 'Atividades recentes' },
  { id: 'cupons-mais-usados', titulo: 'Cupons mais usados' },
  { id: 'clientes-recentes', titulo: 'Últimos clientes' },
];

const MENSAGEM_VAZIO: Record<string, string> = {
  'pedidos-status': 'nenhum pedido cadastrado ainda',
  'pedidos-recentes': 'nenhum pedido cadastrado ainda',
  atividades: 'nenhuma atividade registrada ainda',
  producao: 'nenhuma OPS cadastrada ainda',
  'evolucao-financeira': 'nenhuma transação registrada ainda',
  'origem-vendas': 'nenhuma venda com cupom de parceiro no período',
  'heatmap-envios': 'nenhum envio despachado no período',
  'cupons-mais-usados': 'nenhum cupom utilizado ainda',
  'clientes-recentes': 'nenhum cliente cadastrado ainda',
};

const OPCOES_PERIODO = [3, 6, 12] as const;

/**
 * `<erp-dashboard-page>` — primeira tela real do ERP, agora estruturada
 * em três faixas: KPIs → gráficos principais + blocos independentes
 * (Produção/Estoque/Previsão de caixa) → conteúdo principal (origem de
 * vendas, envios, pedidos recentes) + coluna lateral (atividades,
 * alertas, integrações). Cada indicador/seção continua buscado
 * individualmente via `DashboardApi`/`RelatoriosApi`, nunca com dado
 * fixo — quando o adapter devolve `indisponivel`/`sem-permissao`, a
 * seção mostra isso explicitamente, nunca um número inventado.
 */
export class ErpDashboardPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _dashboardApi: DashboardApi | null = null;
  private _relatoriosApi: RelatoriosApi | null = null;
  private _permissoesRelatorios: PermissoesRelatorios = { ler: false };
  private _societarioApi: SocietarioApi | null = null;
  private _permissoesBonusPg: PermissoesBonusPg = { ler: false, criar: false, atualizar: false };
  private _clientesApi: ClientesApi | null = null;
  /** Memoização de `GET /financeiro/faturamento-por-periodo` — ver `obterFaturamentoPorPeriodoCompartilhado`. Reiniciada ao trocar o filtro de período. */
  private faturamentoPorPeriodoPromise: Promise<Resultado<FaturamentoPeriodo[]>> | null = null;
  private periodoMeses: (typeof OPCOES_PERIODO)[number] = 6;
  private blocosVisiveis = carregarBlocosVisiveis();
  private avisosResultado: Resultado<ItemAviso[]> = { status: 'carregando' };
  private centralDeComandoResultado: Resultado<CentralDeComando> = { status: 'carregando' };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set dashboardApi(valor: DashboardApi) {
    this._dashboardApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set relatoriosApi(valor: RelatoriosApi) {
    this._relatoriosApi = valor;
    if (this.isConnected) void this.carregarRelatorios();
  }

  set permissoesRelatorios(valor: PermissoesRelatorios) {
    this._permissoesRelatorios = valor;
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
    if (this.isConnected) void this.carregarBonusPgBloco();
  }

  set permissoesBonusPg(valor: PermissoesBonusPg) {
    this._permissoesBonusPg = valor;
  }

  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
    if (this.isConnected) void this.carregarClientesRecentes();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._dashboardApi) void this.carregar();
    if (this._relatoriosApi) void this.carregarRelatorios();
    if (this._societarioApi) void this.carregarBonusPgBloco();
    if (this._clientesApi) void this.carregarClientesRecentes();
  }

  // ---------------------------------------------------------------------
  // Esqueleto / layout
  // ---------------------------------------------------------------------

  private renderEsqueleto(): void {
    const agora = new Date();
    const subtitulo = `Visão geral da operação — atualizado às ${agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;

    const kpis = `
      <pg-kpi-card id="kpi-clientes" label="Clientes" icone="usuarios" estado="carregando"></pg-kpi-card>
      <pg-kpi-card id="kpi-pedidos" label="Pedidos" icone="carrinho" estado="carregando"></pg-kpi-card>
      <pg-kpi-card id="kpi-ticket-medio-mes" label="Ticket médio (mês)" icone="dinheiro" estado="carregando"></pg-kpi-card>
      <pg-kpi-card id="kpi-producao" label="Produção" icone="producao" estado="carregando"></pg-kpi-card>
      <pg-kpi-card id="kpi-receita-mes" label="Receita do mês" icone="dinheiro" estado="carregando"></pg-kpi-card>
      <pg-kpi-card id="kpi-receita-ano" label="Faturamento anual" icone="dinheiro" estado="carregando"></pg-kpi-card>
      <pg-kpi-card id="kpi-lucro-mes" label="Lucro do mês" icone="tendencia" estado="carregando"></pg-kpi-card>
    `;

    const blocosIndependentes = `
      <pg-card class="bloco-independente" data-bloco="bloco-producao">
        <span slot="header">Produção</span>
        <a slot="header-acao" href="#/producao" class="link-acao">Ver produção →</a>
        <div id="secao-producao-body"><pg-empty-state id="secao-producao" variant="carregando" titulo="Produção"></pg-empty-state></div>
      </pg-card>
      <pg-card class="bloco-independente" data-bloco="bloco-estoque">
        <span slot="header">Estoque</span>
        <a slot="header-acao" href="#/estoque" class="link-acao">Ver estoque →</a>
        <div id="bloco-estoque-body"><pg-empty-state id="bloco-estoque" variant="carregando" titulo="Estoque"></pg-empty-state></div>
      </pg-card>
      <pg-card class="bloco-independente" data-bloco="bloco-previsao-caixa">
        <span slot="header">Previsão de caixa</span>
        <a slot="header-acao" href="#/financeiro" class="link-acao">Ver financeiro →</a>
        <div id="bloco-previsao-caixa-body"><pg-empty-state id="bloco-previsao-caixa" variant="carregando" titulo="Previsão de caixa"></pg-empty-state></div>
      </pg-card>
      <pg-card class="bloco-independente" data-bloco="bloco-bonus-pg">
        <span slot="header">Bônus PG</span>
        <a slot="header-acao" href="#/bonus-pg" class="link-acao">Ver bônus PG →</a>
        <div id="bloco-bonus-pg-body"><pg-empty-state id="bloco-bonus-pg" variant="carregando" titulo="Bônus PG"></pg-empty-state></div>
      </pg-card>
      <pg-card class="bloco-independente" data-bloco="bloco-clientes-resumo">
        <span slot="header">Clientes &amp; Cupons</span>
        <a slot="header-acao" href="#/clientes" class="link-acao">Ver clientes →</a>
        <div id="bloco-clientes-resumo-body"><pg-empty-state id="bloco-clientes-resumo" variant="carregando" titulo="Clientes &amp; Cupons"></pg-empty-state></div>
      </pg-card>
    `;

    // Mapeia o id interno da seção para o id de `BLOCOS_PERSONALIZAVEIS`
    // (`dashboard.preferencias.ts`) — só 'atividades' diverge (lá é
    // 'atividades-recentes', rótulo mais claro no painel "Personalizar").
    const idBlocoPersonalizavel = (id: string): string => (id === 'atividades' ? 'atividades-recentes' : id);

    const secaoCard = (id: string, titulo: string, acao?: { rotulo: string; caminho: string }): string => `
      <pg-card class="secao" data-bloco="${idBlocoPersonalizavel(id)}">
        <span slot="header">${titulo}</span>
        ${acao ? `<a slot="header-acao" href="#${acao.caminho}" class="link-acao">${acao.rotulo} →</a>` : ''}
        <div id="secao-${id}-body">
          <pg-empty-state id="secao-${id}" variant="carregando" titulo="${titulo}"></pg-empty-state>
        </div>
      </pg-card>`;

    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .cabecalho {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: var(--pg-space-4, 16px);
          flex-wrap: wrap;
          margin-bottom: var(--pg-space-6, 32px);
        }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0;
        }
        .subtitulo {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          margin-top: 2px;
        }
        .acoes-cabecalho { display: flex; align-items: center; gap: var(--pg-space-3, 12px); flex-wrap: wrap; }
        .seletor-periodo {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-family: var(--pg-font-familia-base, system-ui);
          font-size: var(--pg-font-size-sm, 0.875rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .seletor-periodo:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-3, 12px);
        }
        .kpis-nota {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-desabilitado, #5c626b);
          margin: 0 0 var(--pg-space-6, 32px);
        }
        .graficos-principais {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-4, 16px);
        }
        .blocos-independentes {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-6, 32px);
        }
        .layout-principal {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
          gap: var(--pg-space-4, 16px);
          align-items: start;
        }
        @media (max-width: 900px) {
          .layout-principal { grid-template-columns: 1fr; }
        }
        .coluna-principal, .coluna-lateral { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); min-width: 0; }
        .link-acao {
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-medio, 500);
          color: var(--pg-color-dourado, #c9a24b);
          text-decoration: none;
          white-space: nowrap;
        }
        .link-acao:hover { text-decoration: underline; }
        .link-acao:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
          border-radius: var(--pg-radius-sm, 6px);
        }
        .bloco-independente[data-bloco] { display: var(--exibir, block); }
        .estoque-resumo, .previsao-resumo { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); }
        .estoque-numero, .previsao-numero {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-2xl, 2rem);
          font-weight: var(--pg-font-weight-bold, 700);
          font-variant-numeric: tabular-nums;
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .estoque-linha { display: flex; align-items: center; justify-content: space-between; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .estoque-topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-2, 8px); }
        .alertas-lista, .integracoes-lista { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); }
        .alerta-item { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-3, 12px); padding: var(--pg-space-2, 8px) 0; border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); }
        .alerta-item:last-child { border-bottom: none; }
        .alerta-titulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); }
        ${CSS_SITE_TRAFEGO}
      </style>
      <div class="cabecalho">
        <div>
          <h1>Dashboard</h1>
          <div class="subtitulo">${escaparHtml(subtitulo)}</div>
        </div>
        <div class="acoes-cabecalho">
          <select class="seletor-periodo" id="seletor-periodo" title="Período — afeta evolução financeira, origem de vendas e envios">
            ${OPCOES_PERIODO.map((m) => `<option value="${m}" ${m === this.periodoMeses ? 'selected' : ''}>Últimos ${m} meses</option>`).join('')}
          </select>
          <pg-button variant="secondary" id="botao-personalizar">Personalizar</pg-button>
        </div>
      </div>

      <div class="kpis">${kpis}</div>
      <p class="kpis-nota">Receita/lucro do mês, hoje e do ano referem-se sempre ao período corrente — o filtro acima não os afeta, só os gráficos de evolução/origem de vendas/envios abaixo.</p>

      <div class="graficos-principais">
        ${secaoCard('evolucao-financeira', 'Evolução financeira')}
        ${secaoCard('pedidos-status', 'Distribuição de pedidos')}
      </div>

      <div class="blocos-independentes">${blocosIndependentes}</div>

      <div class="layout-principal">
        <div class="coluna-principal">
          ${HTML_CARD_SITE_TRAFEGO}
          ${secaoCard('origem-vendas', 'Origem de vendas por parceiro', { rotulo: 'Ver parceiros', caminho: '/parcerias' })}
          ${secaoCard('cupons-mais-usados', 'Cupons mais usados')}
          ${secaoCard('heatmap-envios', 'Envios despachados (período)', { rotulo: 'Ver envios', caminho: '/envios' })}
          ${secaoCard('pedidos-recentes', 'Pedidos recentes', { rotulo: 'Ver todos', caminho: '/pedidos' })}
          ${secaoCard('clientes-recentes', 'Últimos clientes', { rotulo: 'Ver clientes', caminho: '/clientes' })}
        </div>
        <div class="coluna-lateral">
          <pg-card class="secao" data-bloco="alertas-importantes">
            <span slot="header">Alertas importantes</span>
            <div id="alertas-body"><pg-empty-state id="alertas" variant="carregando" titulo="Alertas importantes"></pg-empty-state></div>
          </pg-card>
          ${secaoCard('atividades', 'Atividades recentes')}
          <pg-card class="secao" data-bloco="integracoes">
            <span slot="header">Integrações</span>
            <div id="integracoes-body">
              <pg-empty-state
                variant="indisponivel"
                titulo="Integrações"
                descricao="Status de credenciais/integrações existe no backend (/core/status), mas essa rota é só de diagnóstico (CLI), nunca exposta à SPA — nenhum status foi inventado aqui."
              ></pg-empty-state>
            </div>
          </pg-card>
        </div>
      </div>

      <pg-modal id="modal-personalizar">
        <span slot="header">Personalizar Dashboard</span>
        <div id="lista-personalizar"></div>
        <div slot="footer">
          <pg-button variant="primary" id="botao-fechar-personalizar">Concluído</pg-button>
        </div>
      </pg-modal>
    `;

    this.aplicarVisibilidadeBlocos();
    this.montarListaPersonalizar();

    this.root.querySelector('#seletor-periodo')?.addEventListener('change', (evento) => {
      const valor = Number((evento.target as HTMLSelectElement).value);
      if (!OPCOES_PERIODO.includes(valor as (typeof OPCOES_PERIODO)[number])) return;
      this.periodoMeses = valor as (typeof OPCOES_PERIODO)[number];
      this.faturamentoPorPeriodoPromise = null;
      void this.carregarEvolucaoFinanceira();
      void this.carregarOrigemVendas();
      void this.carregarHeatmapEnvios();
      void this.carregarFunilProducaoChart();
    });

    const modal = this.root.querySelector('#modal-personalizar') as PgModal;
    this.root.querySelector('#botao-personalizar')?.addEventListener('pg-click', () => modal.setAttribute('open', ''));
    this.root.querySelector('#botao-fechar-personalizar')?.addEventListener('pg-click', () => modal.removeAttribute('open'));
    modal.addEventListener('pg-close', () => modal.removeAttribute('open'));
  }

  /** "Personalizar": mostra/oculta blocos independentes — preferência de UI, nunca esconde ou altera dado (ver `dashboard.preferencias.ts`). */
  private montarListaPersonalizar(): void {
    const lista = this.root.querySelector('#lista-personalizar');
    if (!lista) return;
    lista.innerHTML = BLOCOS_PERSONALIZAVEIS.map(
      (bloco) => `
        <label style="display:flex; align-items:center; gap: var(--pg-space-3, 12px); padding: var(--pg-space-2, 8px) 0; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); cursor: pointer;">
          <input type="checkbox" data-bloco-id="${bloco.id}" ${this.blocosVisiveis.has(bloco.id) ? 'checked' : ''} />
          ${escaparHtml(bloco.rotulo)}
        </label>`
    ).join('');
    lista.querySelectorAll<HTMLInputElement>('input[data-bloco-id]').forEach((input) => {
      input.addEventListener('change', () => {
        const id = input.dataset['blocoId']!;
        if (input.checked) this.blocosVisiveis.add(id);
        else this.blocosVisiveis.delete(id);
        salvarBlocosVisiveis(this.blocosVisiveis);
        this.aplicarVisibilidadeBlocos();
      });
    });
  }

  private aplicarVisibilidadeBlocos(): void {
    this.root.querySelectorAll<HTMLElement>('[data-bloco]').forEach((el) => {
      const id = el.dataset['bloco']!;
      el.style.display = this.blocosVisiveis.has(id) ? '' : 'none';
    });
  }

  // ---------------------------------------------------------------------
  // Carregamento — dados sem RBAC de Relatórios (DashboardApi)
  // ---------------------------------------------------------------------

  private async carregar(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;

    void this.carregarContagem('kpi-clientes', 'clientes', () => api.contarClientes(), 'var(--pg-color-sucesso)');
    void this.carregarProducaoSecao();
    void this.carregarPedidosPorStatusEDonut();
    void this.carregarPedidosRecentes();
    void this.carregarAtividadesRecentes();
    void this.carregarEvolucaoFinanceira();
    void this.carregarAvisos();
    void this.carregarEstoque();
  }

  private async carregarProducaoSecao(): Promise<void> {
    if (this._permissoesRelatorios.ler && this._relatoriosApi) {
      void this.carregarFunilProducaoKpi();
      void this.carregarFunilProducaoChart();
      return;
    }
    const api = this._dashboardApi;
    if (!api) return;
    void this.carregarContagem('kpi-producao', 'produção', () => api.obterResumoProducao(), 'var(--pg-color-destaque)');
    void this.carregarOpsPorEtapa();
  }

  private async carregarRelatorios(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    if (!this._permissoesRelatorios.ler) {
      this.marcarRelatoriosSemPermissao();
      return;
    }

    void this.carregarPulsoDoNegocio();
    void this.carregarPrevisaoCaixaBloco();
    void this.carregarAlertasCentralDeComando();
    void this.carregarOrigemVendas();
    void this.carregarHeatmapEnvios();
    void this.carregarCuponsMaisUsados();
    void this.carregarResumoClientesBloco();
    ligarCardSiteTrafego(this.root, api);
  }

  private marcarRelatoriosSemPermissao(): void {
    const semPermissao: Resultado<never> = { status: 'sem-permissao' };
    for (const id of ['kpi-receita-mes', 'kpi-receita-ano', 'kpi-lucro-mes']) {
      this.aplicarResultadoKpi(id, semPermissao);
    }
    for (const id of ['origem-vendas', 'heatmap-envios', 'cupons-mais-usados']) {
      this.renderSecaoEstado(id, semPermissao);
    }
    this.renderBlocoEstado('bloco-previsao-caixa', semPermissao);
    this.renderBlocoEstado('bloco-clientes-resumo', semPermissao);
    cardSiteTrafegoSemPermissao(this.root);
    // `carregarAlertasCentralDeComando` nunca roda para este papel (sem
    // relatorios:ler) — sem isso, `centralDeComandoResultado` ficaria
    // parado em 'carregando' para sempre e `renderAlertas` nunca
    // renderizaria nada (ver guarda no início do método).
    this.centralDeComandoResultado = semPermissao;
    this.renderAlertas();
  }

  // ---------------------------------------------------------------------
  // KPIs
  // ---------------------------------------------------------------------

  private async carregarPulsoDoNegocio(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterPulsoDoNegocio();
      if (resultado.status !== 'ok') {
        this.aplicarResultadoKpi('kpi-receita-mes', resultado);
        this.aplicarResultadoKpi('kpi-receita-ano', resultado);
        this.aplicarResultadoKpi('kpi-lucro-mes', resultado);
        this.aplicarResultadoKpi('kpi-ticket-medio-mes', resultado);
        return;
      }
      const p = resultado.dado;
      this.definirKpi('kpi-ticket-medio-mes', p.pedidosPagosMes > 0 ? formatBRL(centavosParaReais(p.ticketMedioMesCentavos)) : formatBRL(0));

      // "Faturamento anual" — receita acumulada do ANO corrente, direto da fonte financeira
      // (`PulsoNegocio.receitaAnoCentavos` = SUM de `financeiro.transacoes` receita desde o
      // início do ano, agregado no Postgres, sem limite de página). NUNCA a soma dos meses
      // exibidos no gráfico (o seletor de período mostra 3/6/12 meses, que não cobrem
      // necessariamente o ano-calendário) — é o mesmo número que o Financeiro usa.
      this.definirKpi('kpi-receita-ano', formatBRL(centavosParaReais(p.receitaAnoCentavos)));

      // Tendência/sparkline de Receita e Lucro do mês: derivadas da MESMA
      // série real usada por "Evolução financeira" (receita por mês,
      // `GET /financeiro/faturamento-por-periodo`) — nunca uma fórmula
      // nova. "Lucro" aqui usa `lucroEstimadoCentavos` (margem provisória
      // de 30% sobre a receita, `calcularMargemEstimada`) — nunca
      // `saldoCentavos` (isso é saldo contábil receita−despesa, não
      // lucro); o valor em destaque do card continua sendo
      // `lucroMesCentavos`, a mesma regra aplicada ao mês corrente.
      const periodosResultado = await this.obterFaturamentoPorPeriodoCompartilhado();
      const periodos = periodosResultado.status === 'ok' ? periodosResultado.dado : [];
      const receitaMensal = calcularTendenciaEPontos(periodos.map((per) => per.receitaCentavos));
      const lucroMensal = calcularTendenciaEPontos(periodos.map((per) => per.lucroEstimadoCentavos));

      this.definirKpi('kpi-receita-mes', formatBRL(centavosParaReais(p.receitaMesCentavos)), {
        ...receitaMensal,
        corSparkline: 'var(--pg-color-sucesso)',
      });
      this.definirKpi('kpi-lucro-mes', formatBRL(centavosParaReais(p.lucroMesCentavos)), {
        ...lucroMensal,
        corSparkline: 'var(--pg-color-dourado)',
      });

      // Agregado por MÊS só tem 1 ponto (comum no início de operação, quando toda transação real
      // cai no mesmo mês) — em vez de esperar um 2º mês existir ou inventar um, complementa com uma
      // série de granularidade mais fina (dias, não mês) derivada das transações reais individuais
      // já existentes (`GET /financeiro/transacoes`, mesmo endpoint do módulo Financeiro).
      if (!receitaMensal.pontos || !lucroMensal.pontos) {
        const api2 = this._dashboardApi;
        if (api2) {
          const detalhado = await api2.obterEvolucaoFinanceiraDetalhada();
          if (detalhado.status === 'ok') {
            if (!receitaMensal.pontos) this.definirSparkline('kpi-receita-mes', detalhado.dado.receitaPontos, 'var(--pg-color-sucesso)');
            if (!lucroMensal.pontos) this.definirSparkline('kpi-lucro-mes', detalhado.dado.lucroPontos, 'var(--pg-color-dourado)');
          }
        }
      }
    } catch (erro) {
      logger.erro('falha ao carregar pulso do negócio', { erro });
      const falha: Resultado<never> = { status: 'erro', mensagem: 'falha ao carregar' };
      this.aplicarResultadoKpi('kpi-receita-mes', falha);
      this.aplicarResultadoKpi('kpi-receita-ano', falha);
      this.aplicarResultadoKpi('kpi-lucro-mes', falha);
      this.aplicarResultadoKpi('kpi-ticket-medio-mes', falha);
    }
  }

  /** Bloco independente "Previsão de caixa" — mesmo dado de sempre (`PrevisaoCaixa.confirmadoEmProducaoCentavos`, DEC-033: só "confirmado em produção", sazonalidade não é calculada). */
  private async carregarPrevisaoCaixaBloco(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterPrevisaoCaixa();
      if (resultado.status === 'ok') {
        const body = this.root.querySelector('#bloco-previsao-caixa-body');
        if (!body) return;
        body.innerHTML = `
          <div class="previsao-resumo">
            <span class="previsao-numero">${escaparHtml(formatBRL(centavosParaReais(resultado.dado.confirmadoEmProducaoCentavos)))}</span>
            <span class="estoque-linha"><span>Confirmado em produção, ainda não enviado</span></span>
            <span class="estoque-linha"><span>Pedidos nesta condição</span><strong>${resultado.dado.quantidadePedidos}</strong></span>
          </div>`;
        return;
      }
      this.renderBlocoEstado('bloco-previsao-caixa', resultado);
    } catch (erro) {
      logger.erro('falha ao carregar previsão de caixa', { erro });
      this.renderBlocoEstado('bloco-previsao-caixa', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** Bloco independente "Bônus PG" — resumo administrativo real (contagem por status, `SocietarioApi.listarBonus`), nunca um número calculado: reconhecimento continua 100% discricionário, este bloco só mostra o que já foi registrado manualmente. */
  private async carregarBonusPgBloco(): Promise<void> {
    const api = this._societarioApi;
    if (!api) return;
    if (!this._permissoesBonusPg.ler) {
      this.renderBlocoEstado('bloco-bonus-pg', { status: 'sem-permissao' });
      return;
    }
    try {
      const resultado = await api.listarBonus();
      if (resultado.status === 'ok' || resultado.status === 'vazio') {
        const bonus = resultado.status === 'ok' ? resultado.dado : [];
        const propostos = bonus.filter((b) => b.status === 'proposto').length;
        const aprovados = bonus.filter((b) => b.status === 'aprovado').length;
        const body = this.root.querySelector('#bloco-bonus-pg-body');
        if (!body) return;
        body.innerHTML = `
          <div class="previsao-resumo">
            <span class="previsao-numero">${propostos}</span>
            <span class="estoque-linha"><span>Propostos aguardando aprovação</span></span>
            <span class="estoque-linha"><span>Aprovados aguardando pagamento</span><strong>${aprovados}</strong></span>
          </div>`;
        return;
      }
      this.renderBlocoEstado('bloco-bonus-pg', resultado);
    } catch (erro) {
      logger.erro('falha ao carregar resumo de bônus PG', { erro });
      this.renderBlocoEstado('bloco-bonus-pg', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /**
   * Bloco independente "Clientes & Cupons" — total real e novos no mês
   * (`comercial.clientes`) + pedidos com cupom no mês
   * (`comercial.pedidos.cupom_id`), mesma fonte de
   * `RelatorioService.obterResumoClientes`. Sem "taxa de conversão": a
   * fórmula não está definida em nenhum documento do projeto — mostrar
   * um número aqui seria inventar regra de negócio.
   */
  private async carregarResumoClientesBloco(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterResumoClientes();
      if (resultado.status === 'ok') {
        const body = this.root.querySelector('#bloco-clientes-resumo-body');
        if (!body) return;
        body.innerHTML = `
          <div class="previsao-resumo">
            <span class="previsao-numero">${resultado.dado.totalClientes}</span>
            <span class="estoque-linha"><span>Clientes cadastrados</span></span>
            <span class="estoque-linha"><span>Novos no mês</span><strong>${resultado.dado.novosClientesMes}</strong></span>
            <span class="estoque-linha"><span>Pedidos com cupom (mês)</span><strong>${resultado.dado.pedidosComCupomMes}</strong></span>
          </div>`;
        return;
      }
      this.renderBlocoEstado('bloco-clientes-resumo', resultado);
    } catch (erro) {
      logger.erro('falha ao carregar resumo de clientes', { erro });
      this.renderBlocoEstado('bloco-clientes-resumo', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private renderBlocoEstado(id: string, resultado: Resultado<unknown>): void {
    const body = this.root.querySelector(`#${id}-body`);
    if (!body) return;
    const titulo =
      id === 'bloco-previsao-caixa'
        ? 'Previsão de caixa'
        : id === 'bloco-estoque'
          ? 'Estoque'
          : id === 'bloco-bonus-pg'
            ? 'Bônus PG'
            : id === 'bloco-clientes-resumo'
              ? 'Clientes & Cupons'
              : id;
    const variant = variantDoResultado(resultado);
    body.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(legendaDoResultado(resultado))}"></pg-empty-state>`;
  }

  // ---------------------------------------------------------------------
  // Estoque (bloco independente novo)
  // ---------------------------------------------------------------------

  private async carregarEstoque(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;
    try {
      const resultado = await api.obterResumoEstoque();
      if (resultado.status === 'ok') {
        this.renderEstoque(resultado.dado);
        return;
      }
      if (resultado.status === 'vazio') {
        this.renderEstoque({ totalInsumosAtivos: 0, abaixoDoPontoDeReposicao: 0 });
        return;
      }
      this.renderBlocoEstado('bloco-estoque', resultado);
    } catch (erro) {
      logger.erro('falha ao carregar resumo de estoque', { erro });
      this.renderBlocoEstado('bloco-estoque', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private renderEstoque(resumo: ResumoEstoque): void {
    const body = this.root.querySelector('#bloco-estoque-body');
    if (!body) return;
    const tomAlerta = resumo.abaixoDoPontoDeReposicao > 0 ? 'alerta' : 'sucesso';
    const { tomSaude, textoSaude } = classificarSaudeEstoque(resumo);
    body.innerHTML = `
      <div class="estoque-resumo">
        <div class="estoque-topo">
          <span class="estoque-numero">${resumo.totalInsumosAtivos}</span>
          ${textoSaude ? `<pg-badge tone="${tomSaude}">${textoSaude}</pg-badge>` : ''}
        </div>
        <span class="estoque-linha"><span>Insumos cadastrados</span></span>
        <span class="estoque-linha">
          <span>Abaixo do ponto de reposição</span>
          <pg-badge tone="${tomAlerta}">${resumo.abaixoDoPontoDeReposicao}</pg-badge>
        </span>
      </div>`;
  }

  // ---------------------------------------------------------------------
  // Alertas importantes (Avisos + Central de Comando, combinados)
  // ---------------------------------------------------------------------

  private async carregarAvisos(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;
    try {
      this.avisosResultado = await api.obterAvisos();
      this.renderAlertas();
    } catch (erro) {
      logger.erro('falha ao carregar avisos e pendências', { erro });
      this.avisosResultado = { status: 'erro', mensagem: 'falha ao carregar' };
      this.renderAlertas();
    }
  }

  private async carregarAlertasCentralDeComando(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      this.centralDeComandoResultado = await api.obterCentralDeComando();
      this.renderAlertas();
    } catch (erro) {
      logger.erro('falha ao carregar central de comando', { erro });
      this.centralDeComandoResultado = { status: 'erro', mensagem: 'falha ao carregar' };
      this.renderAlertas();
    }
  }

  /**
   * Combina os dois alertas já reais (`obterAvisos`, escopo mais amplo de
   * papel; `obterCentralDeComando`, exclusivo relatorios:ler) numa única
   * lista visual. Rastreia o `Resultado` de cada fonte, não só os itens —
   * um papel sem acesso a NENHUMA das duas fontes precisa ver
   * "sem permissão", nunca "nenhuma pendência" (que afirmaria, incorreto,
   * que não há pendência real quando na verdade ela só não pode ser vista).
   */
  private renderAlertas(): void {
    const body = this.root.querySelector('#alertas-body');
    if (!body) return;

    if (this.avisosResultado.status === 'carregando' || this.centralDeComandoResultado.status === 'carregando') {
      return;
    }

    const itens: Array<{ titulo: string; quantidade: number; tom: 'alerta' | 'erro' }> = [];
    let algumaFonteAcessivel = false;

    if (this.avisosResultado.status === 'ok') {
      algumaFonteAcessivel = true;
      itens.push(...this.avisosResultado.dado.map((a) => ({ titulo: a.titulo, quantidade: a.quantidade, tom: 'alerta' as const })));
    } else if (this.avisosResultado.status === 'vazio') {
      algumaFonteAcessivel = true;
    }

    if (this.centralDeComandoResultado.status === 'ok') {
      algumaFonteAcessivel = true;
      const c = this.centralDeComandoResultado.dado;
      if (c.comissoesPendentes > 0) itens.push({ titulo: 'Comissões pendentes de pagamento', quantidade: c.comissoesPendentes, tom: 'alerta' });
      if (c.garantiasVencendo !== null && c.garantiasVencendo > 0) itens.push({ titulo: 'Garantias vencendo', quantidade: c.garantiasVencendo, tom: 'erro' });
      if (c.insumosBaixoEstoque > 0) itens.push({ titulo: 'Insumos abaixo do ponto de reposição', quantidade: c.insumosBaixoEstoque, tom: 'erro' });
    } else if (this.centralDeComandoResultado.status === 'vazio') {
      algumaFonteAcessivel = true;
    }

    if (!algumaFonteAcessivel) {
      body.innerHTML = `<pg-empty-state variant="sem-permissao" titulo="Alertas importantes" descricao="você não tem permissão para ver este dado"></pg-empty-state>`;
      return;
    }

    if (itens.length === 0) {
      body.innerHTML = `<pg-empty-state variant="vazio" titulo="Alertas importantes" descricao="nenhuma pendência operacional no momento"></pg-empty-state>`;
      return;
    }

    body.innerHTML = `
      <div class="alertas-lista">
        ${itens
          .map(
            (item) => `
          <div class="alerta-item">
            <span class="alerta-titulo">${escaparHtml(item.titulo)}</span>
            <pg-badge tone="${item.tom}">${item.quantidade}</pg-badge>
          </div>`
          )
          .join('')}
      </div>`;
  }

  // ---------------------------------------------------------------------
  // Gráficos principais
  // ---------------------------------------------------------------------

  /** Linha "Evolução financeira" — mesmo dado que antes alimentava a tabela "Faturamento por período" (removida: mesmo indicador, dois formatos). RBAC própria (`financeiro:ler`), independente de `relatorios:ler` — por isso carregada a partir de `carregar()`, não de `carregarRelatorios()`. */
  private async carregarEvolucaoFinanceira(): Promise<void> {
    try {
      const resultado = await this.obterFaturamentoPorPeriodoCompartilhado();
      this.renderSecaoChart('evolucao-financeira', resultado, (periodos: FaturamentoPeriodo[]) => ({
        // 1ª série = principal no pg-chart (linha mais grossa + área de preenchimento) — Faturamento
        // é a métrica que deve ter mais presença visual; Lucro/Despesas ficam como linhas de apoio.
        series: [
          { key: 'faturamento', label: 'Faturamento', color: 'var(--pg-color-sucesso)' },
          { key: 'lucro', label: 'Lucro', color: 'var(--pg-color-dourado)' },
          { key: 'despesas', label: 'Despesas', color: 'var(--pg-color-erro)' },
        ],
        points: periodos.map((p) => ({
          label: formatarRotuloPeriodo(p.periodo),
          values: { faturamento: p.receitaCentavos, lucro: p.lucroEstimadoCentavos, despesas: p.despesaCentavos },
        })),
        formatarValor: (centavos: number) => formatBRL(centavosParaReais(centavos)),
        tipo: 'line' as const,
      }));
    } catch (erro) {
      logger.erro('falha ao carregar evolução financeira', { erro });
      this.renderSecaoEstado('evolucao-financeira', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** Distribuição de pedidos por status — mesmo dado que antes só virava tabela; agora também alimenta o donut (`pg-chart type="donut"`). */
  private async carregarPedidosPorStatusEDonut(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;
    try {
      const resultado = await api.obterPedidosPorStatus();
      if (resultado.status === 'ok') {
        const total = STATUS_PEDIDO.reduce((soma, opcao) => soma + resultado.dado.contagem[opcao.valor], 0);
        this.definirKpi('kpi-pedidos', String(total), resultado.dado.aproximado ? { dica: `${total}+ (primeira página)` } : undefined);
        // Sparklines de "Pedidos" e "Ticket médio" — derivadas da MESMA lista de pedidos já buscada
        // aqui para o donut (nenhuma chamada nova): contagem por período para Pedidos, média de
        // `valorTotalCentavos` dos pagos para Ticket médio.
        this.definirSparkline('kpi-pedidos', bucketarContagem(resultado.dado.pedidos, (p) => p.criadoEm), 'var(--pg-color-sucesso)');
        const pagos = resultado.dado.pedidos.filter((p) => p.status === 'pago');
        this.definirSparkline(
          'kpi-ticket-medio-mes',
          bucketarMedia(pagos, (p) => p.criadoEm, (p) => p.valorTotalCentavos),
          'var(--pg-color-sucesso)'
        );
      } else {
        this.aplicarResultadoKpi('kpi-pedidos', resultado);
      }
      const CORES_STATUS: Record<string, string> = { pago: 'var(--pg-color-sucesso)', pendente: 'var(--pg-color-alerta)', cancelado: 'var(--pg-color-erro)' };
      this.renderSecaoChart('pedidos-status', resultado, (dado) => ({
        series: [{ key: 'valor', label: '' }],
        points: STATUS_PEDIDO.map((opcao) => ({
          label: opcao.rotulo,
          values: { valor: dado.contagem[opcao.valor] },
          color: CORES_STATUS[opcao.valor]!,
        })),
        formatarValor: (valor: number) => String(valor),
        tipo: 'donut' as const,
      }));
    } catch (erro) {
      this.aplicarResultadoKpi('kpi-pedidos', { status: 'erro', mensagem: 'falha ao carregar' });
      logger.erro('falha ao carregar pedidos por status', { erro });
      this.renderSecaoEstado('pedidos-status', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarOrigemVendas(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterOrigemVendasPorParceiro(this.periodoMeses);
      this.renderSecaoChart('origem-vendas', resultado, (origem: OrigemVendaParceiro[]) => ({
        series: [{ key: 'valor', label: 'Vendas' }],
        points: origem.map((o) => ({ label: o.parceiroNome, values: { valor: o.valorCentavos } })),
        formatarValor: (centavos: number) => formatBRL(centavosParaReais(centavos)),
      }));
    } catch (erro) {
      logger.erro('falha ao carregar origem de vendas por parceiro', { erro });
      this.renderSecaoEstado('origem-vendas', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** "Cupons mais usados" — top N por contagem real de pedidos que aplicaram o cupom (`RelatorioService.obterCuponsMaisUsados`), sem filtro de período: é um ranking histórico, não uma série temporal. */
  private async carregarCuponsMaisUsados(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterCuponsMaisUsados(5);
      this.renderSecaoChart('cupons-mais-usados', resultado, (cupons: CupomMaisUsado[]) => ({
        series: [{ key: 'usos', label: 'Usos' }],
        points: cupons.map((c) => ({ label: c.codigo, values: { usos: c.usos } })),
        formatarValor: (valor: number) => String(valor),
      }));
    } catch (erro) {
      logger.erro('falha ao carregar cupons mais usados', { erro });
      this.renderSecaoEstado('cupons-mais-usados', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /**
   * KPI "Produção" no topo = OPS ativas AGORA, sem filtro de período (é um retrato do pipeline
   * neste instante, não um dado histórico — por isso não muda com o seletor de período do
   * Dashboard). O gráfico "Produção por etapa" abaixo, por outro lado, respeita o período
   * selecionado (`carregarFunilProducaoChart`) — são duas consultas separadas de propósito.
   */
  private async carregarFunilProducaoKpi(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterFunilProducao();
      if (resultado.status === 'ok') {
        const ativas = ETAPAS_ROTULO.filter((opcao) => opcao.valor !== ETAPA_CONCLUSAO).reduce(
          (soma, opcao) => soma + resultado.dado[opcao.valor],
          0
        );
        this.definirKpi('kpi-producao', String(ativas));
        // Sparkline: `obterFunilProducao` só devolve o total agregado por etapa (sem os registros
        // individuais), então não dá pra derivar uma série dele. `obterResumoProducao` (mesmo
        // endpoint `GET /ops` já usado no caminho sem `relatorios:ler` logo abaixo) traz a listagem
        // completa — usamos só o `pontos` dela aqui; o total exibido continua vindo do funil acima.
        if (this._dashboardApi) {
          const resumo = await this._dashboardApi.obterResumoProducao();
          if (resumo.status === 'ok') this.definirSparkline('kpi-producao', resumo.dado.pontos, 'var(--pg-color-destaque)');
        }
      } else {
        this.aplicarResultadoKpi('kpi-producao', resultado);
      }
    } catch (erro) {
      logger.erro('falha ao carregar KPI de produção', { erro });
      this.aplicarResultadoKpi('kpi-producao', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarFunilProducaoChart(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterFunilProducao(this.periodoMeses * 30);
      this.renderSecaoChart('producao', resultado, (funil) => ({
        series: [{ key: 'quantidade', label: 'OPS' }],
        points: ETAPAS_ROTULO.map((opcao) => ({ label: opcao.rotulo, values: { quantidade: funil[opcao.valor] } })),
        formatarValor: (valor: number) => String(valor),
      }));
    } catch (erro) {
      logger.erro('falha ao carregar gráfico de produção por etapa', { erro });
      this.renderSecaoEstado('producao', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarHeatmapEnvios(): Promise<void> {
    const api = this._relatoriosApi;
    if (!api) return;
    try {
      const resultado = await api.obterHeatmapEnvios(this.periodoMeses * 30);
      if (resultado.status === 'ok') {
        const body = this.root.querySelector('#secao-heatmap-envios-body');
        if (!body) return;
        body.innerHTML = '<pg-chart></pg-chart>';
        const chart = body.querySelector('pg-chart') as PgChart;
        chart.type = 'heatmap';
        chart.dias = this.periodoMeses * 30;
        chart.cells = resultado.dado;
        return;
      }
      this.renderSecaoEstado('heatmap-envios', resultado);
    } catch (erro) {
      logger.erro('falha ao carregar heatmap de envios', { erro });
      this.renderSecaoEstado('heatmap-envios', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private renderSecaoChart<T>(
    id: string,
    resultado: Resultado<T>,
    paraChart: (dado: T) => { series: PgChart['series']; points: PgChartPoint[]; formatarValor: (valor: number) => string; tipo?: 'bar' | 'line' | 'donut' }
  ): void {
    if (resultado.status === 'ok') {
      const body = this.root.querySelector(`#secao-${id}-body`);
      if (!body) return;
      const { series, points, formatarValor, tipo } = paraChart(resultado.dado);
      body.innerHTML = '<pg-chart></pg-chart>';
      const chart = body.querySelector('pg-chart') as PgChart;
      chart.type = tipo ?? 'bar';
      chart.series = series;
      chart.formatarValor = formatarValor;
      chart.points = points;
      return;
    }
    this.renderSecaoEstado(id, resultado);
  }

  private obterFaturamentoPorPeriodoCompartilhado(): Promise<Resultado<FaturamentoPeriodo[]>> {
    const api = this._dashboardApi;
    if (!api) return Promise.resolve({ status: 'erro', mensagem: 'dashboardApi não inicializada' });
    if (!this.faturamentoPorPeriodoPromise) {
      this.faturamentoPorPeriodoPromise = api.obterFaturamentoPorPeriodo(this.periodoMeses);
    }
    return this.faturamentoPorPeriodoPromise;
  }

  private async carregarPedidosRecentes(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;
    try {
      this.renderSecaoTabela('pedidos-recentes', await api.obterPedidosRecentes(), (pedidos) => {
        const colunas: PgTableColumn[] = [
          { key: 'id', label: 'ID' },
          { key: 'status', label: 'Status' },
          { key: 'valorTotal', label: 'Valor', align: 'right' },
          { key: 'criadoEm', label: 'Criado em' },
        ];
        const linhas = pedidos.map(pedidoParaLinhaResumida);
        return { colunas, linhas };
      });
    } catch (erro) {
      logger.erro('falha ao carregar pedidos recentes', { erro });
      this.renderSecaoEstado('pedidos-recentes', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** "Últimos clientes" — mesmo endpoint de `<erp-clientes-page>` (`ClientesApi.listar`), ordenado por `criado_em DESC` no backend (`comercial.clientes`); aqui só limita a 5, nenhuma consulta nova. */
  private async carregarClientesRecentes(): Promise<void> {
    const api = this._clientesApi;
    if (!api) return;
    try {
      this.renderSecaoTabela('clientes-recentes', await api.listar({ limit: 5 }), (clientes) => {
        const colunas: PgTableColumn[] = [
          { key: 'nome', label: 'Nome' },
          { key: 'email', label: 'E-mail' },
          { key: 'criadoEm', label: 'Cadastrado em' },
        ];
        const linhas = clientes.map(clienteParaLinhaResumida);
        return { colunas, linhas };
      });
    } catch (erro) {
      logger.erro('falha ao carregar últimos clientes', { erro });
      this.renderSecaoEstado('clientes-recentes', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarOpsPorEtapa(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;
    try {
      this.renderSecaoTabela('producao', await api.obterOpsPorEtapa(), (resultado) => {
        const colunas: PgTableColumn[] = [
          { key: 'etapa', label: 'Etapa' },
          { key: 'quantidade', label: 'Quantidade', align: 'right' },
        ];
        const linhas = ETAPAS_ROTULO.map((opcao) => ({
          etapa: opcao.rotulo,
          quantidade: String(resultado.contagem[opcao.valor]),
        }));
        return { colunas, linhas };
      });
    } catch (erro) {
      logger.erro('falha ao carregar produção por etapa', { erro });
      this.renderSecaoEstado('producao', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarAtividadesRecentes(): Promise<void> {
    const api = this._dashboardApi;
    if (!api) return;
    try {
      this.renderSecaoTabela('atividades', await api.obterAtividadesRecentes(), (registros) => {
        const colunas: PgTableColumn[] = [
          { key: 'acao', label: 'Ação' },
          { key: 'ator', label: 'Ator' },
          { key: 'quando', label: 'Quando' },
        ];
        const linhas = registros.map((registro) => ({
          acao: registro.acao,
          ator: registro.ator === 'system' ? 'sistema' : registro.ator,
          quando: formatarDataHora(registro.criadoEm),
        }));
        return { colunas, linhas };
      });
    } catch (erro) {
      logger.erro('falha ao carregar atividades recentes', { erro });
      this.renderSecaoEstado('atividades', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  // ---------------------------------------------------------------------
  // Helpers genéricos de estado (mesma regra em toda a página: nunca
  // mostrar número como se fosse real quando o resultado não é 'ok')
  // ---------------------------------------------------------------------

  private renderSecaoTabela<T>(
    id: string,
    resultado: Resultado<T>,
    paraTabela: (dado: T) => { colunas: PgTableColumn[]; linhas: Record<string, string>[] }
  ): void {
    if (resultado.status === 'ok') {
      const body = this.root.querySelector(`#secao-${id}-body`);
      if (!body) return;
      const { colunas, linhas } = paraTabela(resultado.dado);
      body.innerHTML = '<pg-table></pg-table>';
      const tabela = body.querySelector('pg-table') as PgTable;
      tabela.columns = colunas;
      tabela.rows = linhas;
      return;
    }
    this.renderSecaoEstado(id, resultado);
  }

  private renderSecaoEstado(id: string, resultado: Resultado<unknown>): void {
    const body = this.root.querySelector(`#secao-${id}-body`);
    if (!body) return;
    const titulo = SECOES.find((s) => s.id === id)?.titulo ?? '';
    const variant = variantDoResultado(resultado);
    const descricao = resultado.status === 'vazio' ? MENSAGEM_VAZIO[id] ?? 'nenhum registro encontrado' : legendaDoResultado(resultado);
    body.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private async carregarContagem(
    idKpi: string,
    rotulo: string,
    buscar: () => Promise<Resultado<Contagem>>,
    corSparkline?: string
  ): Promise<void> {
    try {
      const resultado = await buscar();
      if (resultado.status === 'ok') {
        this.definirKpi(idKpi, String(resultado.dado.total), {
          ...(resultado.dado.aproximado ? { dica: `${resultado.dado.total}+ (primeira página)` } : {}),
          ...(resultado.dado.pontos && corSparkline ? { pontos: resultado.dado.pontos, corSparkline } : {}),
        });
        return;
      }
      this.aplicarResultadoKpi(idKpi, resultado);
    } catch (erro) {
      logger.erro(`falha ao carregar KPI de ${rotulo}`, { erro });
      this.aplicarResultadoKpi(idKpi, { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private definirKpi(
    id: string,
    valor: string,
    opts?: { dica?: string; tendencia?: string; tendenciaDirecao?: 'alta' | 'baixa' | 'neutra'; pontos?: number[]; corSparkline?: string }
  ): void {
    const card = this.root.querySelector(`#${id}`) as PgKpiCard | null;
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
    if (opts?.dica) card.setAttribute('dica', opts.dica);
    if (opts?.tendencia) {
      card.setAttribute('tendencia', opts.tendencia);
      card.setAttribute('tendencia-direcao', opts.tendenciaDirecao ?? 'neutra');
    }
    if (opts?.pontos && opts.pontos.length >= 2) card.pontos = opts.pontos;
    if (opts?.corSparkline) card.corSparkline = opts.corSparkline;
  }

  /**
   * Define só a sparkline de um KPI já renderizado, sem tocar `estado`/`valor` (evita corrida com
   * a chamada que define o valor do card, que roda em paralelo e não necessariamente termina
   * primeiro — `pg-kpi-card` já sabe re-renderizar a cada propriedade setada independentemente).
   */
  private definirSparkline(id: string, pontos: number[] | undefined, cor: string): void {
    if (!pontos || pontos.length < 2) return;
    const card = this.root.querySelector(`#${id}`) as PgKpiCard | null;
    if (!card) return;
    card.pontos = pontos;
    card.corSparkline = cor;
  }

  private aplicarResultadoKpi(id: string, resultado: Resultado<unknown>): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;

    if (resultado.status === 'vazio') {
      card.setAttribute('estado', 'ok');
      card.setAttribute('valor', '0');
      return;
    }
    card.setAttribute('estado', 'indisponivel');
    card.setAttribute('dica', legendaDoResultado(resultado));
  }
}

/** Deriva tendência (% vs. período anterior) + pontos de sparkline de uma série real já buscada — nunca calcula nada quando a série é curta ou o denominador é zero (sem percentual sem sentido). */
function calcularTendenciaEPontos(serieCentavos: number[]): { tendencia?: string; tendenciaDirecao?: 'alta' | 'baixa' | 'neutra'; pontos?: number[] } {
  if (serieCentavos.length < 2) return {};
  const atual = serieCentavos[serieCentavos.length - 1]!;
  const anterior = serieCentavos[serieCentavos.length - 2]!;
  const pontos = serieCentavos;
  if (anterior === 0) return { pontos };
  const variacao = ((atual - anterior) / Math.abs(anterior)) * CENTAVOS_POR_REAL;
  const direcao: 'alta' | 'baixa' | 'neutra' = variacao > 0.5 ? 'alta' : variacao < -0.5 ? 'baixa' : 'neutra';
  return { tendencia: formatarPercentual(variacao), tendenciaDirecao: direcao, pontos };
}

function formatarPercentual(valor: number): string {
  const sinal = valor > 0 ? '+' : '';
  return `${sinal}${valor.toFixed(1).replace('.', ',')}%`;
}

function variantDoResultado(resultado: Resultado<unknown>): 'sem-permissao' | 'erro' | 'vazio' | 'indisponivel' {
  return resultado.status === 'sem-permissao'
    ? 'sem-permissao'
    : resultado.status === 'erro'
      ? 'erro'
      : resultado.status === 'vazio'
        ? 'vazio'
        : 'indisponivel';
}

/** Mesmo formato de `pedidoParaLinha` em `<erp-pedidos-page>` — id abreviado, sem coluna de cliente (não é o foco desta seção resumida). */
function pedidoParaLinhaResumida(pedido: Pedido): Record<string, string> {
  return {
    id: pedido.id.length > 8 ? `${pedido.id.slice(0, 8)}…` : pedido.id,
    status: rotuloStatusPedido(pedido.status),
    valorTotal: formatBRL(centavosParaReais(pedido.valorTotalCentavos)),
    criadoEm: formatarDataHora(pedido.criadoEm),
  };
}

/** Mesmo formato de `<erp-clientes-page>` para a coluna "Cadastrado em" — nome/e-mail direto, sem coluna de classificação (não é o foco desta seção resumida). */
function clienteParaLinhaResumida(cliente: Cliente): Record<string, string> {
  return {
    nome: cliente.nome,
    email: cliente.email ?? '—',
    criadoEm: formatarDataHora(cliente.criadoEm),
  };
}

/** Classificação de saúde do estoque — só a partir de `ResumoEstoque` real, nenhum dado inventado. Sem insumo cadastrado não tem "saúde" (badge omitido). */
function classificarSaudeEstoque(resumo: ResumoEstoque): { tomSaude: 'sucesso' | 'alerta' | 'erro'; textoSaude: string } {
  if (resumo.totalInsumosAtivos === 0) return { tomSaude: 'sucesso', textoSaude: '' };
  const proporcao = resumo.abaixoDoPontoDeReposicao / resumo.totalInsumosAtivos;
  if (proporcao === 0) return { tomSaude: 'sucesso', textoSaude: 'Saudável' };
  if (proporcao <= 0.5) return { tomSaude: 'alerta', textoSaude: 'Em atenção' };
  return { tomSaude: 'erro', textoSaude: 'Crítico' };
}

/** "2026-08" -> "ago/2026" (rótulo do eixo X da Evolução financeira). Formatos inesperados voltam como vieram. */
function formatarRotuloPeriodo(periodo: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (!m) return periodo;
  const [, ano, mes] = m;
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  const rotulo = data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');
  return rotulo;
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

function legendaDoResultado(resultado: Resultado<unknown>): string {
  switch (resultado.status) {
    case 'indisponivel':
      return resultado.motivo;
    case 'sem-permissao':
      return 'você não tem permissão para ver este dado';
    case 'erro':
      return resultado.mensagem;
    default:
      return 'módulo ainda não implementado';
  }
}

customElements.define('erp-dashboard-page', ErpDashboardPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-dashboard-page': ErpDashboardPage;
  }
}
